/** Shared notification list labels — aligned with prod LLP notifTo/From columns. */

import { roleLabelForDisplay } from 'utils/entityTerminology';

export const NOTIFICATION_STATUS_FILTER_OPTIONS = [
  { text: 'All', value: '' },
  { text: 'Pending', value: 'pending' },
  { text: 'Accepted', value: 'accepted' },
  { text: 'Auto Accepted', value: 'auto_accepted' },
  { text: 'Declined', value: 'declined' },
  { text: 'Expired', value: 'expired' },
];

/** Outgoing (/notification/to) — prod excludes request on sent list for most roles. */
export const NOTIFICATION_INVITE_TYPE_OUTGOING_FILTER = [
  { text: 'All', value: '' },
  { text: 'Add Property', value: 'add' },
  { text: 'Auto Add', value: 'auto_add' },
  { text: 'Reinvite', value: 'reinvite' },
  { text: 'Invite', value: 'invite' },
  { text: 'Moved', value: 'moved' },
  { text: 'Remove', value: 'remove' },
];

/** Received (/notification/from) — includes delegation request type. */
export const NOTIFICATION_INVITE_TYPE_RECEIVED_FILTER = [
  { text: 'All', value: '' },
  { text: 'Request', value: 'request' },
  { text: 'Add Property', value: 'add' },
  { text: 'Auto Add', value: 'auto_add' },
  { text: 'Invite', value: 'invite' },
  { text: 'Moved', value: 'moved' },
  { text: 'Remove', value: 'remove' },
];

export const NOTIFICATION_INVITE_TYPES = [
  { text: 'ADD PROPERTY', value: 'add', badge: 'neutral' },
  { text: 'AUTO ADD', value: 'auto_add', badge: 'purple' },
  { text: 'REQUEST', value: 'request', badge: 'warmBlue' },
  { text: 'REINVITE', value: 'reinvite', badge: 'parisGreen' },
  { text: 'INVITE', value: 'invite', badge: 'tomato' },
  { text: 'MOVED', value: 'moved', badge: 'sunglow' },
  { text: 'REMOVE', value: 'remove', badge: 'warmBlue' },
];

export const NOTIFICATION_REQUEST_STATUSES = [
  { text: 'Declined', value: 'declined', badge: 'neutral' },
  { text: 'Pending', value: 'pending', badge: 'purple' },
  { text: 'Accepted', value: 'accepted', badge: 'parisGreen' },
  { text: 'Auto Accepted', value: 'auto_accepted', badge: 'parisGreen' },
  { text: 'Expired', value: 'expired', badge: 'tomato' },
];

export const getInviteTypeMeta = (value) =>
  NOTIFICATION_INVITE_TYPES.find((item) => item.value === value);

export const getRequestStatusMeta = (value) =>
  NOTIFICATION_REQUEST_STATUSES.find((item) => item.value === value) || {
    text: value,
    value,
    badge: 'neutral',
  };

export const getRoleLabel = (role) => roleLabelForDisplay(role) || 'Property Manager';

export const getRoleBadgeColor = (role) => {
  if (role === 'll') return 'warmBlue';
  if (role === 'vp') return 'tomato';
  if (role === 'rm') return 'purpleIrish';
  if (role === 'admin') return 'neutral';
  return 'magentaRed';
};

const NOTIFICATION_SORT_FIELDS = new Set([
  'request_type',
  'request_status',
  'accepter_email',
  'accepter_role',
  'requestor_email',
  'requestor_role',
  'email_text',
  'updatedAt',
  'property.length',
]);

/** BE expects filter.options as an array of { name, values } — not an object. */
export const buildNotificationApiFilter = (
  baseFilter = {},
  { accepterEmails = [], requestorEmails = [], properties = [], statuses = [], types = [] } = {},
) => ({
  searchText: baseFilter?.searchText ?? '',
  searchPlaceholder: baseFilter?.searchPlaceholder ?? 'Search Notification',
  options: [
    { name: 'accepter_email', values: accepterEmails },
    { name: 'requestor_email', values: requestorEmails },
    { name: 'property', values: properties },
    { name: 'request_status', values: statuses },
    { name: 'request_type', values: types },
  ],
});

/** Strip invalid sort keys before POST /notification/to|from (BE orders by column name). */
export const sanitizeNotificationSortOptions = (sortOptions = {}) => {
  if (!sortOptions || typeof sortOptions !== 'object') return {};
  return Object.fromEntries(
    Object.entries(sortOptions).filter(
      ([field, direction]) =>
        field &&
        field !== 'undefined' &&
        NOTIFICATION_SORT_FIELDS.has(field) &&
        (direction === 1 || direction === -1),
    ),
  );
};

/** Default list request when saga refresh has no stored query (e.g. after resend). */
export const getDefaultNotificationListRequest = ({
  offset = 0,
  limit = 12,
  sortOptions = {},
  filter,
} = {}) => ({
  filter: filter ?? buildNotificationApiFilter(),
  offset,
  limit,
  sortOptions: sanitizeNotificationSortOptions(sortOptions),
});
